import { supabase } from '@/lib/supabase'

const toNumber = (value) => {
  const nextValue = Number(value)
  return Number.isFinite(nextValue) ? nextValue : null
}

const toText = (value) => {
  const nextValue = String(value || '').trim()
  return nextValue || ''
}

const toSnapshot = (state) => ({
  userId: state.userId,
  isActive: state.isActive,
  latitude: state.latitude,
  longitude: state.longitude,
  city: state.city,
  country: state.country,
})

const toRadians = (value) => (Number(value) * Math.PI) / 180

const haversineDistanceKm = (latitudeA, longitudeA, latitudeB, longitudeB) => {
  if (![latitudeA, longitudeA, latitudeB, longitudeB].every(Number.isFinite)) return Number.POSITIVE_INFINITY
  const earthRadiusKm = 6371
  const latitudeDelta = toRadians(latitudeB - latitudeA)
  const longitudeDelta = toRadians(longitudeB - longitudeA)
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(toRadians(latitudeA)) * Math.cos(toRadians(latitudeB)) * Math.sin(longitudeDelta / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadiusKm * c
}

const state = {
  userId: '',
  isActive: false,
  latitude: null,
  longitude: null,
  city: '',
  country: '',
  watchId: null,
  syncTimeoutId: null,
  lastSentAt: 0,
  lastSentCoords: null,
  syncInFlight: false,
  subscribers: new Set(),
}

const notifySubscribers = () => {
  const snapshot = toSnapshot(state)
  state.subscribers.forEach((listener) => {
    try {
      listener(snapshot)
    } catch (_error) {
      // Ignore subscriber errors so tracking continues.
    }
  })
}

const shouldSendLiveLocationUpdate = (latitude, longitude) => {
  const now = Date.now()
  if (!state.lastSentCoords) return true
  if (now - state.lastSentAt >= 20000) return true

  const distanceKm = haversineDistanceKm(
    state.lastSentCoords.latitude,
    state.lastSentCoords.longitude,
    latitude,
    longitude
  )

  return distanceKm >= 0.05
}

const persistLiveLocationUpdate = async () => {
  if (state.syncInFlight || !state.isActive || !state.userId) return
  if (!Number.isFinite(state.latitude) || !Number.isFinite(state.longitude)) return

  state.syncInFlight = true

  try {
    const latitude = Number(state.latitude)
    const longitude = Number(state.longitude)
    const { error } = await supabase
      .from('drive_radar_presence')
      .update({
        latitude,
        longitude,
        city: toText(state.city) || null,
        country: toText(state.country) || null,
      })
      .eq('user_id', state.userId)

    if (!error) {
      state.lastSentAt = Date.now()
      state.lastSentCoords = { latitude, longitude }
    }
  } finally {
    state.syncInFlight = false
  }
}

const queueLiveLocationSync = () => {
  if (!state.isActive || !state.userId || typeof window === 'undefined') return

  if (state.syncTimeoutId) {
    window.clearTimeout(state.syncTimeoutId)
  }

  state.syncTimeoutId = window.setTimeout(() => {
    state.syncTimeoutId = null
    persistLiveLocationUpdate()
  }, 4000)
}

const handlePosition = (position) => {
  const latitude = toNumber(position?.coords?.latitude)
  const longitude = toNumber(position?.coords?.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return

  state.latitude = latitude
  state.longitude = longitude
  notifySubscribers()

  if (!shouldSendLiveLocationUpdate(latitude, longitude)) return
  queueLiveLocationSync()
}

const stopWatcher = () => {
  if (typeof navigator !== 'undefined' && navigator.geolocation && state.watchId !== null) {
    navigator.geolocation.clearWatch(state.watchId)
  }

  state.watchId = null

  if (state.syncTimeoutId && typeof window !== 'undefined') {
    window.clearTimeout(state.syncTimeoutId)
  }
  state.syncTimeoutId = null
}

const ensureWatcher = () => {
  if (!state.isActive || !state.userId) {
    stopWatcher()
    return
  }

  if (state.watchId !== null) return
  if (typeof navigator === 'undefined' || !navigator.geolocation) return

  state.watchId = navigator.geolocation.watchPosition(
    (position) => {
      handlePosition(position)
    },
    () => {
      // Keep the last known location if continuous tracking fails.
    },
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 10000,
    }
  )
}

export const subscribeToDriveRadarLiveLocation = (listener) => {
  if (typeof listener !== 'function') return () => {}

  state.subscribers.add(listener)
  listener(toSnapshot(state))

  return () => {
    state.subscribers.delete(listener)
  }
}

export const syncDriveRadarLiveLocation = ({
  userId = '',
  isActive = false,
  latitude,
  longitude,
  city,
  country,
  markAsPersisted = false,
} = {}) => {
  state.userId = String(userId || '').trim()
  state.isActive = Boolean(isActive && state.userId)

  if (latitude !== undefined) {
    state.latitude = toNumber(latitude)
  }
  if (longitude !== undefined) {
    state.longitude = toNumber(longitude)
  }
  if (city !== undefined) {
    state.city = toText(city)
  }
  if (country !== undefined) {
    state.country = toText(country)
  }

  if (!state.isActive) {
    stopWatcher()
    state.lastSentAt = 0
    state.lastSentCoords = null
  } else if (markAsPersisted && Number.isFinite(state.latitude) && Number.isFinite(state.longitude)) {
    state.lastSentAt = Date.now()
    state.lastSentCoords = {
      latitude: Number(state.latitude),
      longitude: Number(state.longitude),
    }
  }

  ensureWatcher()
  notifySubscribers()
}
